import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ConversaMensagem } from '../../interfaces/conversa-mensagem';
import { ConversaMidia } from '../../interfaces/conversa';
import { ConversaService } from '../../services/conversa.service';

/** Estado da mídia de uma mensagem enquanto ela é buscada e depois de chegar. */
interface MidiaAberta {
  carregando: boolean;
  url?: string;
  contentType?: string;
  fileName?: string;
  erro?: string;
}

/**
 * Balões de uma conversa de WhatsApp.
 *
 * Existe para ter um só desenho de conversa no sistema: o popup do cadastro e a caixa de entrada
 * mostram a mesma coisa, e duas cópias divergiriam no primeiro ajuste de balão.
 *
 * Não busca a lista: recebe pronta, em ordem cronológica. Mas busca a mídia, sim — é o clique de
 * quem quer ouvir o áudio que dispara o download na Evolution, e isso pertence ao balão, não à tela.
 */
@Component({
  selector: 'app-conversa-mensagens',
  templateUrl: './conversa-mensagens.component.html',
  styleUrls: ['./conversa-mensagens.component.scss']
})
export class ConversaMensagensComponent {

  /** Em ordem cronológica: a mais antiga primeiro, como se lê. */
  @Input() mensagens: ConversaMensagem[] = [];

  @Input() carregando = false;

  @Input() vazioTitulo = 'Nenhuma conversa registrada.';

  @Input() vazioDica = 'As mensagens aparecem aqui conforme chegam pelo WhatsApp.';

  /** Fundo da conversa. Desligado quando quem usa já pinta a área. */
  @Input() comFundo = true;

  /**
   * Oferece reenviar o que falhou. Ligado só onde há como enviar — no popup da ficha do cliente, que
   * é leitura, um botão de reenviar prometeria o que a tela não faz.
   */
  @Input() permiteReenviar = false;

  @Output() reenviar = new EventEmitter<ConversaMensagem>();

  /** Mostra o botão de anexar o arquivo ao cadastro, lançamento ou agenda. */
  @Input() permiteAnexar = false;

  /** Quem hospeda decide para onde vai: só ele conhece o contexto da conversa. */
  @Output() anexarArquivo = new EventEmitter<ConversaMensagem>();

  /**
   * Mídias já abertas, por id de mensagem.
   *
   * Guardado aqui e não na mensagem porque a lista é substituída a cada atualização automática: no
   * objeto, o áudio que o atendente está ouvindo se perderia a cada seis segundos.
   */
  midias: { [mensagemId: number]: MidiaAberta } = {};

  constructor(private conversaService: ConversaService) { }

  /**
   * Identidade do balão para o Angular reaproveitar o DOM.
   *
   * Sem isto a lista inteira é recriada a cada atualização automática — e o áudio que o atendente
   * está ouvindo voltaria ao início a cada seis segundos. O balão otimista ainda não tem id, então
   * cai na posição, que basta enquanto ele existe.
   */
  chaveDaMensagem(indice: number, m: ConversaMensagem): string | number {
    if (m?.id) {
      return m.id;
    }
    return m?.messageId || 'pendente-' + indice;
  }

  /**
   * Primeira mensagem do dia: é onde entra a faixa com a data.
   *
   * Sem isso, conversa de meses vira um rolo sem referência de tempo — e o atendente responde
   * achando que a última pergunta foi hoje.
   */
  abreODia(indice: number): boolean {
    const atual = this.diaDe(this.mensagens[indice]);
    if (!atual) {
      return false;
    }
    if (indice === 0) {
      return true;
    }
    return atual !== this.diaDe(this.mensagens[indice - 1]);
  }

  /** "Hoje", "Ontem" ou a data — o que o WhatsApp faz, porque é o que se lê sem pensar. */
  rotuloDoDia(m: ConversaMensagem): string {
    const dia = this.diaDe(m);
    if (!dia) {
      return '';
    }
    const ontem = new Date();
    ontem.setDate(ontem.getDate() - 1);
    if (dia === new Date().toDateString()) {
      return 'Hoje';
    }
    if (dia === ontem.toDateString()) {
      return 'Ontem';
    }
    return new Date(m.timestamp)
      .toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  private diaDe(m: ConversaMensagem): string {
    if (!m?.timestamp) {
      return '';
    }
    const data = new Date(m.timestamp);
    return isNaN(data.getTime()) ? '' : data.toDateString();
  }

  falhou(m: ConversaMensagem): boolean {
    return m?.sendStatus === 'FAILED' || m?.sendStatus === 'ERROR';
  }

  pedirReenvio(m: ConversaMensagem): void {
    if (this.permiteReenviar && this.falhou(m)) {
      this.reenviar.emit(m);
    }
  }

  recebida(m: ConversaMensagem): boolean {
    return m?.direction === 'INBOUND';
  }

  /** Ícone de entrega no padrão do WhatsApp, só para o que saiu. */
  iconeStatus(m: ConversaMensagem): string {
    switch (m?.sendStatus) {
      case 'READ':
      case 'DELIVERED':
        return 'done_all';
      case 'SENT':
        return 'done';
      case 'FAILED':
      case 'ERROR':
        return 'error_outline';
      default:
        return 'schedule';
    }
  }

  /**
   * Texto do balão.
   *
   * Em mídia com legenda o conteúdo é a legenda; sem legenda não há texto nenhum a mostrar, porque o
   * próprio arquivo aparece acima.
   */
  conteudo(m: ConversaMensagem): string {
    if (m?.messageContent) {
      return m.messageContent;
    }
    if (this.temMidia(m)) {
      return '';
    }
    return m?.messageType ? `[${m.messageType.toLowerCase()}]` : '[sem conteúdo]';
  }

  /** O rótulo "[áudio]" que a API grava não é legenda: era só um resumo para a lista. */
  private legendaDeServico(m: ConversaMensagem): boolean {
    const texto = (m?.messageContent || '').trim();
    return texto.startsWith('[') && texto.endsWith(']');
  }

  temMidia(m: ConversaMensagem): boolean {
    if (!m) {
      return false;
    }
    if (m.comMidia !== undefined) {
      return m.comMidia;
    }
    // Mensagem que o front acabou de criar (balão otimista) não passou pela API e não traz a marca.
    return ['IMAGE', 'AUDIO', 'VIDEO', 'DOCUMENT', 'STICKER'].indexOf(m.messageType) >= 0;
  }

  /** Legenda que vale mostrar: a de serviço ("[áudio]") não. */
  legenda(m: ConversaMensagem): string {
    if (!m?.messageContent || this.legendaDeServico(m)) {
      return '';
    }
    return m.messageContent;
  }

  midia(m: ConversaMensagem): MidiaAberta {
    return this.midias[m?.id] || { carregando: false };
  }

  /** Busca o arquivo e deixa o player/imagem visível. Segunda vez não busca de novo. */
  abrirMidia(m: ConversaMensagem): void {
    if (!m?.id || !this.temMidia(m)) {
      return;
    }
    const atual = this.midias[m.id];
    if (atual && (atual.carregando || atual.url)) {
      return;
    }
    this.midias[m.id] = { carregando: true };
    this.conversaService.getMidia(m.id).subscribe({
      next: (midia: ConversaMidia) => {
        this.midias[m.id] = {
          carregando: false,
          url: midia?.url,
          contentType: midia?.contentType,
          fileName: midia?.fileName,
        };
      },
      error: erro => {
        this.midias[m.id] = {
          carregando: false,
          // 410 é o caso comum: mídia antiga sai do servidor do WhatsApp.
          erro: erro?.status === 410
            ? 'Esta mídia não está mais disponível no WhatsApp.'
            : 'Não foi possível abrir a mídia.',
        };
      }
    });
  }

  baixar(m: ConversaMensagem): void {
    const aberta = this.midias[m?.id];
    if (aberta?.url) {
      window.open(aberta.url, '_blank');
      return;
    }
    if (!m?.id) {
      return;
    }
    this.midias[m.id] = { carregando: true };
    this.conversaService.getMidia(m.id).subscribe({
      next: (midia: ConversaMidia) => {
        this.midias[m.id] = { carregando: false, url: midia?.url, fileName: midia?.fileName };
        if (midia?.url) {
          window.open(midia.url, '_blank');
        }
      },
      error: () => {
        this.midias[m.id] = { carregando: false, erro: 'Não foi possível baixar o arquivo.' };
      }
    });
  }

  /** Rótulo do botão antes de abrir: diz o que vai acontecer, não só o tipo. */
  acaoDaMidia(m: ConversaMensagem): string {
    switch (m?.messageType) {
      case 'AUDIO':
        return 'Ouvir áudio';
      case 'IMAGE':
        return 'Ver imagem';
      case 'STICKER':
        return 'Ver figurinha';
      case 'VIDEO':
        return 'Ver vídeo';
      default:
        return 'Baixar arquivo';
    }
  }

  iconeDaMidia(m: ConversaMensagem): string {
    switch (m?.messageType) {
      case 'AUDIO':
        return 'play_circle_outline';
      case 'IMAGE':
      case 'STICKER':
        return 'image';
      case 'VIDEO':
        return 'movie';
      default:
        return 'description';
    }
  }
}
